import React, {
  useCallback, useEffect, useMemo, useState, Component,
} from 'react';
import {
  Grid, Button, Container, useTheme, Alert,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { withApollo } from 'hoc/withApollo';
import Events from 'containers/layouts/agendaPage/Events';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client/react';
import moment from 'moment';
import Drawer from '@mui/material/Drawer';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import useMediaQuery from '@mui/material/useMediaQuery';
import { RRule } from 'rrule';
import dynamic from 'next/dynamic';
import ButtonGroupSelected from '../../../components/buttons/ButtonGroupSelected';
import Calendar from '../../../components/Calendar';
import Filters from '../../../components/filters';
import { rruleToText } from '../../../utils/utils';

const MapWithNoSSR = dynamic(() => import('../../../components/map/Map'), {
  ssr: false,
});

const MarkerWithNoSSR = dynamic(() => import('../../../components/map/EventMarker'), {
  ssr: false,
});

const MarkerClusterWithNoSSR = dynamic(() => import('../../../components/map/MarkerCluster'), {
  ssr: false,
});

let matchesWindow = false;
if (typeof window !== 'undefined') {
  matchesWindow = window.matchMedia('(max-width: 600px)').matches;
}

const drawerWidth = 310;

const GET_EVENTS = gql`
  query events($startingDate: String, $search: String, $entries: [[String]], $favoritesForUser: String, $periodicEvent: Boolean, $lat: Float, $lng: Float, $radius: Float) {
    events(startingDate: $startingDate, search: $search, entries: $entries, favoritesForUser: $favoritesForUser, periodicEvent: $periodicEvent, lat: $lat, lng: $lng, radius: $radius) {
      id
      label
      startedAt
      endedAt
      dateRule
      published
      lat
      lng
      address
      city
      shortDescription
      url
      favorites{
        id
      }
      entries {
        label
        icon
        collection {
          code
          label
        }
        parentEntry {
          code
          label
          color
          collection {
            code
            label
          }
        }
      }
      actors {
        id
        name
      }
      pictures {
        id
        label
        originalPicturePath
        originalPictureFilename
        position
        logo
        main
      }
      parentEvent {
        id
        label
      }
    }
  }
`;

interface StyleProps {
  isMenuOpen: boolean;
  isMapMode: boolean;
}

const useStyles = makeStyles<StyleProps>()((theme, { isMenuOpen, isMapMode }) => ({
  '@media print': {
    drawer: {
      display: 'none !important',
    },
    filterButton: {
      display: 'none',
    },
    layout: {
      backgroundColor: 'white !important',
      display: 'block !important',
    },
  },
  main: {
    padding: '0',
    margin: '0',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    maxWidth: 'none',
    width: '100%',
  },
  layout: {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.grey,
    padding: '0',
    margin: '0',
    width: '100%',
    maxWidth: 'none',
    position: 'relative',
    overflow: 'hidden',
    flex: 1,
    minHeight: 0,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
      height: 'auto',
      flex: 'none',
    },
  },
  drawer: {
    width: isMenuOpen ? (isMapMode ? 0 : drawerWidth) : 0,
    flexShrink: 0,
    transition: isMapMode ? undefined : theme.transitions.create(['width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  drawerPaper: {
    position: 'absolute',
    width: drawerWidth,
    height: '100%',
    maxHeight: '100%',
    top: 0,
    overflowY: 'auto',
  },
  filterButton: {
    position: 'absolute',
    left: isMenuOpen ? drawerWidth : 20,
    bottom: 20,
    zIndex: 1000,
    borderTopLeftRadius: isMenuOpen ? 0 : 4,
    borderBottomLeftRadius: isMenuOpen ? 0 : 4,
    transition: theme.transitions.create(['left'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down('sm')]: {
      position: 'fixed',
      bottom: 10,
      left: 10,
    },
  },
  filterButtonIcon: {
    transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  },
  listButtonIcon: {
    marginRight: 10,
  },
  image: {
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    textAlign: 'inherit',
    height: '10em',
  },
  categorie: {
    backgroundColor: 'white',
    borderRadius: '0.3em',
    color: '#f0a300',
    width: 'max-content',
    padding: '0 5px 0 5px',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
  },
  title: {
    textAlign: 'left',
    color: '#2C367E',
    width: '100%',
  },
  buttonGrid: {
    margin: '2.5em 0 2.5em 0 ',
    color: 'white',
    'background-color': '#2C367E',
    border: 'none',
    borderRadius: '1.5em',
    padding: '0 3em 0 3em',
    height: '2.5em',
    '&:hover': {
      cursor: 'pointer',
      color: '#2C367E',
      'background-color': 'white',
      border: '2px solid #2C367E',
      backgroundImage: "url('./arrow-hover.svg')",
    },
    backgroundImage: "url('./arrow.svg')",
    backgroundRepeat: 'no-repeat',
    'background-position-x': '5px',
    'background-position-y': '1px',
    'background-size': '14%',
  },
  favorite: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  favoriteIcon: {
    color: '#AD2740',
  },
  mapContainer: {
    height: '100% !important',
    [theme.breakpoints.down('sm')]: {
      height: '80vh !important',
    },
  },
}));

const VIEW_STATE = {
  LIST: 'LIST',
  MAP: 'MAP',
  CALENDAR: 'CALENDAR',
} as const;

type AgendaCalendarBoundaryProps = {
  children: React.ReactNode;
};

type AgendaCalendarBoundaryState = {
  hasError: boolean;
  errorMessage?: string;
};

class AgendaCalendarBoundary extends Component<AgendaCalendarBoundaryProps, AgendaCalendarBoundaryState> {
  constructor(props: AgendaCalendarBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): AgendaCalendarBoundaryState {
    return { hasError: true, errorMessage: error?.message || 'Erreur inconnue' };
  }

  componentDidCatch(error: Error) {
    console.error('Agenda calendar render error:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          Impossible d'afficher le calendrier pour le moment.
          {this.state.errorMessage ? ` (${this.state.errorMessage})` : ''}
        </Alert>
      );
    }

    return this.props.children;
  }
}

const parseTimestamp = (value: any): number | null => {
  const timestamp = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const normalizeAgendaDate = (value: any): Date | null => {
  const normalizedDate = new Date(value);

  if (Number.isNaN(normalizedDate.getTime())) {
    return null;
  }

  normalizedDate.setUTCHours(0, 0, 0, 0);
  return normalizedDate;
};

const AgendaPageLayout = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<string>(VIEW_STATE.LIST);
  const [filters, setFilters] = useState<any>(null);
  const [recuringEvent, setRecuringEvent] = useState(true);
  const [searchDate, setSearchDate] = useState<Date>(() => normalizeAgendaDate(new Date()) || new Date());
  const [isMenuOpen, setIsMenuOpen] = useState(!matchesWindow);

  const isListMode = useMemo(() => viewMode === VIEW_STATE.LIST, [viewMode]);
  const isMapMode = useMemo(() => viewMode === VIEW_STATE.MAP, [viewMode]);
  const isCalendarMode = useMemo(() => viewMode === VIEW_STATE.CALENDAR, [viewMode]);

  const { classes } = useStyles({ isMenuOpen, isMapMode });

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
  }, [isMenuOpen]);

  useEffect(() => {
    const handler = () => setIsMenuOpen(true);
    window.addEventListener('ouaaa:open-filters', handler);
    return () => window.removeEventListener('ouaaa:open-filters', handler);
  }, []);

  const { data: eventData, loading: loadingEvents, refetch } = useQuery(GET_EVENTS, {
    variables: {
      startingDate: moment.utc(searchDate).startOf('day').toISOString(),
      periodicEvent: recuringEvent,
    },
  });

  const handleFiltersChange = useCallback(async (newFilters: any) => {
    const nextFilters = { ...newFilters };
    const rawStartingDate = nextFilters.startingDate?.$d || nextFilters.startingDate;
    let nextSearchDate = searchDate;

    if (rawStartingDate !== undefined) {
      const selectedDate = normalizeAgendaDate(rawStartingDate);

      if (selectedDate) {
        nextSearchDate = selectedDate;
        nextFilters.startingDate = selectedDate.toISOString();
      } else {
        delete nextFilters.startingDate;
      }
    }
    setSearchDate(nextSearchDate);
    setFilters(nextFilters);

    const nextRecurringEvent = nextFilters.periodicEvent !== undefined
      ? nextFilters.periodicEvent
      : recuringEvent;

    if (nextFilters.periodicEvent !== undefined) {
      setRecuringEvent(nextFilters.periodicEvent);
    }

    const { locationLat, locationLng, locationRadius, locationLabel, ...restFilters } = nextFilters;
    const defaultStartingDate = moment.utc(nextSearchDate).startOf('day').toISOString();
    const refetchVariables: any = {
      startingDate: defaultStartingDate,
      search: null,
      entries: null,
      favoritesForUser: null,
      lat: null,
      lng: null,
      radius: null,
      ...restFilters,
      periodicEvent: nextRecurringEvent,
    };
    if (locationLat != null && locationLng != null && locationRadius != null) {
      refetchVariables.lat = locationLat;
      refetchVariables.lng = locationLng;
      refetchVariables.radius = locationRadius;
    }
    await refetch(refetchVariables);
  }, [recuringEvent, refetch, searchDate]);

  const getAllEventsFromRecurringEvent = (event: any, rangeStartDate: Date) => {
    const startTimestamp = parseTimestamp(event.startedAt);
    if (!event?.dateRule || startTimestamp === null) {
      return [];
    }

    const startEventDate = moment(startTimestamp);
    const { dateRule } = event;
    try {
      const rrule = RRule.fromString(`DTSTART:${startEventDate.format('YYYYMMDD[T]HHmmss[Z]')}\nRRULE:${dateRule}`);
      const rangeStart = moment.utc(rangeStartDate).startOf('day');
      const rangeEnd = rangeStart.clone().add(1, 'month');

      return rrule.between(rangeStart.toDate(), rangeEnd.toDate(), true).map((date) => {
        return {
          ...event,
          startedAt: moment(date).valueOf().toString(),
          duration: rruleToText(rrule),
        };
      });
    } catch (error) {
      console.error('Invalid recurring event skipped on agenda page', {
        eventId: event?.id,
        dateRule: event?.dateRule,
        startedAt: event?.startedAt,
        error,
      });
      return [];
    }
  };

  const fabActions = useMemo(() => {
    return [
      { name: 'list', label: 'Liste', onClick: () => setViewMode(VIEW_STATE.LIST) },
      { name: 'map', label: 'Carte', onClick: () => setViewMode(VIEW_STATE.MAP) },
      { name: 'calendar', label: 'Calendrier', onClick: () => setViewMode(VIEW_STATE.CALENDAR) },
    ];
  }, []);

  const events = useMemo(() => {
    const initialEvents = eventData?.events || [];
    const referenceDate = moment.max(moment(), moment.utc(searchDate).startOf('day'));

    const recurringEvents = initialEvents.filter((event: any) => event.dateRule);
    const allRecurringEvents = !recuringEvent
      ? []
      : recurringEvents.map((evt: any) => getAllEventsFromRecurringEvent(evt, searchDate));

    const filteredInitial = initialEvents.filter((event: any) => !event.dateRule);

    const filteredRecurring = allRecurringEvents.reduce((acc: any[], items: any[]) => {
      const filteredItems = items.filter((event: any) => {
        const start = moment(parseInt(event.startedAt, 10));
        let end = null;
        if (event.endedAt) {
          end = moment(parseInt(event.endedAt, 10));
        }

        const startsInFuture = referenceDate.isSameOrBefore(start);
        const notEndedYet = end && referenceDate.isSameOrBefore(end);

        return startsInFuture || notEndedYet;
      });

      return acc.concat(filteredItems);
    }, []);

    return filteredInitial.concat(filteredRecurring);
  }, [eventData, recuringEvent, searchDate]);

  const calendarEvents = useMemo(() => {
    const initialEvents = (eventData?.events || []);

    return initialEvents.reduce((acc: any[], evt: any) => {
      const startTimestamp = parseTimestamp(evt.startedAt);
      const endTimestamp = parseTimestamp(evt.endedAt ?? evt.startedAt);

      if (startTimestamp === null || endTimestamp === null) {
        console.error('Agenda event ignored in calendar due to invalid timestamps', {
          eventId: evt?.id,
          startedAt: evt?.startedAt,
          endedAt: evt?.endedAt,
        });
        return acc;
      }

      const startDate = new Date(startTimestamp);
      const endDate = new Date(endTimestamp);

      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        console.error('Agenda event ignored in calendar due to invalid Date', {
          eventId: evt?.id,
          startedAt: evt?.startedAt,
          endedAt: evt?.endedAt,
        });
        return acc;
      }

      let dateRule = {};
      if (evt.dateRule) {
        dateRule = {
          rRule: evt.dateRule,
        };
      }

      acc.push({
        startDate,
        endDate,
        title: evt.label,
        id: evt.id,
        url: evt.url,
        location: evt.city ? [evt.address, evt.city].join(', ') : '',
        backgroundColor: evt?.entries?.[0]?.parentEntry?.color || 'blue',
        ...dateRule,
      });

      return acc;
    }, []);
  }, [eventData]);

  return (
    <Container className={classes.main}>
      <Container className={classes.layout}>
        <ButtonGroupSelected buttons={fabActions} />

        <Drawer
          anchor="left"
          variant={matches ? 'temporary' : 'persistent'}
          open={isMenuOpen}
          className={classes.drawer}
          classes={{
            paper: classes.drawerPaper,
          }}
          onClose={toggleMenu}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{ 'data-tour': 'filter-panel' } as any}
        >
          <Filters
            isEventList
            onFiltersChange={handleFiltersChange}
            isCalendarMode={isCalendarMode}
            closeHandler={toggleMenu}
          />
        </Drawer>

        {
          (!matches || !isMenuOpen) && (
            <Button
              variant="contained"
              className={classes.filterButton}
              onClick={toggleMenu}
              startIcon={<DoubleArrowIcon className={classes.filterButtonIcon} />}
              color="primary"
              data-tour="filter-toggle"
            >
              Filtres
            </Button>
          )
        }

        {isListMode && (
          <Events events={events} loading={loadingEvents || isLoading} />
        )}

        {isCalendarMode && (
          <AgendaCalendarBoundary>
            <Calendar events={calendarEvents} />
          </AgendaCalendarBoundary>
        )}

        {isMapMode && (
          <Grid style={{ width: '100%' }}>
            <MapWithNoSSR scrollWheelZoom={true} classMap={classes.mapContainer}>
              <MarkerClusterWithNoSSR>
                {typeof eventData !== 'undefined'
                  && eventData.events.map((event: any, index: number) => {
                    if (event.lat != null && event.lng != null && !isNaN(event.lat) && !isNaN(event.lng)) {
                      return (
                        <MarkerWithNoSSR key={event.id} event={event} />
                      );
                    }
                  })}
              </MarkerClusterWithNoSSR>
            </MapWithNoSSR>
          </Grid>
        )}
      </Container>
    </Container>
  );
};

export default withApollo()(AgendaPageLayout);
